import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { canTransition, jobMachine, type JobState } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';

/**
 * Moving a job through its life (V5 dogfood — these procedures existed on the server from
 * M1 and had no button anywhere in v2, so a job could be created and worked but never
 * finished).
 *
 * Which buttons appear is derived from the SHARED state machine, not from a hand-written
 * list of statuses. The server rejects an illegal transition either way; deriving here
 * means the screen can never offer one it will refuse, and a new transition shows up in
 * the UI the day it is added to the machine.
 */

const ACTIONS: {
  via: string;
  label: string;
  /** Confirm first when the step is hard to walk back. */
  confirm?: string;
  variant?: 'primary' | 'ghost' | 'danger';
}[] = [
  { via: 'job.completeWork', label: 'Work is done', variant: 'primary' },
  { via: 'job.close', label: 'Close it out', variant: 'primary' },
  { via: 'job.hold', label: 'Put on hold', variant: 'ghost' },
  { via: 'job.resume', label: 'Take off hold', variant: 'ghost' },
  {
    via: 'job.cancel',
    label: 'Cancel work order',
    variant: 'ghost',
    confirm: "Cancel this work order? It can't be un-cancelled.",
  },
];

export function JobActions({ jobId, status }: { jobId: string; status: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const opts = {
    onSuccess: () => {
      setError(null);
      setConfirming(null);
      void qc.invalidateQueries();
    },
    onError: (e: { message: string }) => {
      setError(e.message);
      setConfirming(null);
    },
  };
  const completeWork = useMutation(trpc.job.completeWork.mutationOptions(opts));
  const close = useMutation(trpc.job.close.mutationOptions(opts));
  const hold = useMutation(trpc.job.hold.mutationOptions(opts));
  const resume = useMutation(trpc.job.resume.mutationOptions(opts));
  const cancel = useMutation(trpc.job.cancel.mutationOptions(opts));

  const byVia: Record<string, { mutate: (v: never) => void; isPending: boolean }> = {
    'job.completeWork': completeWork,
    'job.close': close,
    'job.hold': hold,
    'job.resume': resume,
    'job.cancel': cancel,
  };
  const busy = Object.values(byVia).some((m) => m.isPending);

  // `system: true` transitions belong to the server (a visit starting, an invoice issuing);
  // offering them as buttons would let a human fake an event that never happened.
  const available = ACTIONS.filter((a) => {
    const t = jobMachine.transitions.find(
      (x) => x.via === a.via && x.from === (status as JobState),
    );
    return t && !t.system && canTransition(jobMachine, status as JobState, t.to);
  });

  if (available.length === 0) return null;

  return (
    <div className="space-y-2">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {confirming ? (
        <Notice tone="warn">{ACTIONS.find((a) => a.via === confirming)?.confirm}</Notice>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {available.map((a) => {
          const m = byVia[a.via]!;
          const armed = confirming === a.via;
          return (
            <Button
              key={a.via}
              variant={armed ? 'danger' : (a.variant ?? 'ghost')}
              disabled={busy}
              onClick={() => {
                if (a.confirm && !armed) {
                  setConfirming(a.via);
                  return;
                }
                m.mutate({ jobId, ...idem() } as never);
              }}
            >
              {armed ? 'Yes, cancel it' : a.label}
            </Button>
          );
        })}
        {confirming ? (
          <Button variant="ghost" onClick={() => setConfirming(null)}>
            Never mind
          </Button>
        ) : null}
      </div>
    </div>
  );
}
